import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminMultimediaImageGalleria } from '../../models/admin-multimedia-image-galleria';
import { AdminMultimediaGalleriaService } from '../../services/admin-multimedia-galleria.service';
import { MultimediaGridLayoutComponent } from '../../components/multimedia-grid-layout/multimedia-grid-layout.component';
import { MultimediaGridLayoutItemComponent } from '../../components/multimedia-grid-layout-item/multimedia-grid-layout-item.component';
import { Multimedia } from '../../../shared/models/multimedia';
import { MultimediaTileComponent } from '../../../shared/components/multimedia-tile/multimedia-tile.component';
import { Observable } from 'rxjs';
import { AdminMultimediaDetails } from '../../models/admin-multimedia-details';
import { Guid } from '@web/utils';
import { MultimediaTileMode } from '../../../shared/types/multimedia-tile-mode';

@Component({
  selector: 'app-multimedia-galleria-thumbnails',
  imports: [
    CommonModule,
    MultimediaGridLayoutComponent,
    MultimediaGridLayoutItemComponent,
    MultimediaTileComponent,
  ],
  templateUrl: './multimedia-galleria-thumbnails.component.html',
  styleUrl: './multimedia-galleria-thumbnails.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaGalleriaThumbnailsComponent {
  private readonly multimediaGalleria = inject(AdminMultimediaGalleriaService);

  /* prettier-ignore */
  public readonly fetchMultimediaDetailsFn = input.required<(id: Guid) => Observable<AdminMultimediaDetails>>();
  /* prettier-ignore */
  public readonly templateColumns = input('sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-3 2xl:grid-cols-4');
  public readonly files = input.required<Multimedia[]>();
  public readonly images = this.imagesSignal();
  public readonly documents = this.documentsSignal();
  public readonly others = this.othersSignal();

  public handlePreviewClick(index: number): void {
    this.multimediaGalleria.show(
      new AdminMultimediaImageGalleria({
        images: this.images(),
        activeIndex: index,
      }),
    );
  }

  protected fetchMultimediaDetails(
    id: Guid,
  ): Observable<AdminMultimediaDetails> {
    const fn = this.fetchMultimediaDetailsFn();

    return fn(id);
  }

  protected mode(multimedia: AdminMultimediaDetails): MultimediaTileMode {
    if (multimedia.isDocument || multimedia.isArchive) {
      return 'download';
    }

    return 'preview';
  }

  private imagesSignal(): Signal<Multimedia[]> {
    return computed(() => this.files().filter((file) => file.isImage));
  }

  private documentsSignal(): Signal<Multimedia[]> {
    return computed(() => this.files().filter((file) => file.isDocument));
  }

  /* prettier-ignore */
  private othersSignal(): Signal<Multimedia[]> {
    return computed(() => this.files().filter((file) => !file.isImage && !file.isDocument));
  }
}
